import React, { useState, useEffect, useRef } from 'react';
import {
  Button, Modal, ModalHeader, ModalBody, ModalFooter,
  Input, Label, FormGroup, Row, Col, InputGroup, InputGroupText,
} from 'reactstrap';
import DatePicker from 'react-datepicker';
import { DebounceInput } from 'react-debounce-input';
import Select from 'react-select';
import { useQuery } from '@apollo/client';
import { CITIES, NEIGHBORHOODS, ZIP_CODES, PROPERTY_POLICIES } from '@api';
import LocationMapPanel from './LocationMapPanel';
import { defaultAmenities, unitTypes } from '@sparklib';
import { Icon } from '@assets';
import Nouislider from 'nouislider-react';
import moment from 'moment';

const BED_OPTIONS = Object.entries(unitTypes);
const BATH_OPTIONS = ['1', '1.5', '2', '2.5', '3'];
const numberFormatter = new Intl.NumberFormat('en-US');
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
});

// ── FilterPill ──────────────────────────────────────────────────────────────
function FilterPill({ onClick, resetFilter, filterLabel, filterValue, children, active, mobile, canClear }) {
  // canClear overrides the default truthy-filterValue check for the × button.
  // Used by the Location pill where the city always displays but a city-only
  // state shouldn't be considered "clearable" (clearing would just snap to default).
  const showClear = canClear !== undefined ? canClear : Boolean(filterValue);
  if (mobile) {
    const hasValue = Boolean(filterValue);
    return (
      <div className={`bg-pill bg-pill-mobile ${active ? 'active' : ''}`}>
        <div className='bg-pill-header' onClick={onClick}>
          <div className='bg-pill-header-text'>
            <h6 className='font-weight-bold mb-0'>{filterLabel}</h6>
            {hasValue && <div className='filter-value text-muted'>{filterValue}</div>}
          </div>
          <div className='bg-pill-header-action'>
            {showClear ? (
              <button
                type='button'
                className='clear-filter-button'
                aria-label={`Clear ${filterLabel}`}
                onClick={(e) => { e.stopPropagation(); resetFilter(e); }}
              >
                <Icon icon='times' />
              </button>
            ) : (
              <Icon icon='chevron-down' className='accordion-chevron' />
            )}
          </div>
        </div>
        {active && (
          <div className='filter-box-container--inline'>{children}</div>
        )}
      </div>
    );
  }
  return (
    <div className={`bg-pill justify-content-between ${active ? 'active' : ''}`}>
      <div
        className={`d-flex flex-column position-relative justify-content-center h-100 w-100 bg-pill-header${filterValue && active ? ' clear' : ''}`}
        onClick={onClick}
      >
        <div className='text-left text-nowrap mt-2'>
          <h6 className='font-weight-bold'>{filterLabel}</h6>
        </div>
        <div className='align-items-center text-muted text-truncate filter-value'>
          <h6 className='font-weight-bold'>
            {filterValue || <div style={{ paddingLeft: '1px' }}>—</div>}
          </h6>
        </div>
      </div>
      <div onClick={resetFilter} className={`clear-filter-button position-relative${!(showClear && active) ? ' hidden' : ''}`}>
        <Icon icon='times' style={{ fontSize: '0.8rem', opacity: 0.6 }} />
      </div>
      <div>{children}</div>
      <hr />
    </div>
  );
}

// ── Content components (module-level for stable references) ─────────────────
// Defined outside FilterBar so their function reference is stable across
// re-renders. Components defined inside a parent get a new reference on every
// render — React unmounts + remounts them, killing input focus and resetting
// local state. Module-level components re-render in place, preserving both.

function LocationContent({ filters, setFilters, cityOptions, neighborhoodOptions, zipOptions, onExpandChange, totalMatched }) {
  const selectedCity = cityOptions.find((o) => o.value === filters.cityId) || null;
  const selectedNeighborhoods = neighborhoodOptions.filter((o) => filters.neighborhoodIds.includes(o.value));
  const selectedZips = zipOptions.filter((o) => filters.zipCodes.includes(o.value));

  const selectStyles = { menuPortal: (base) => ({ ...base, zIndex: 9999 }) };

  return (
    <div className='filter-box-container' style={{ left: 0 }}>
      <div className='filter-box location-filter-box' style={{ overflowY: 'visible', overflowX: 'visible' }}>
        {/* Row 1: City · Neighborhoods · ZIP Codes */}
        <div className='location-dropdowns-row mb-3'>
          <div className='location-dropdown-col location-dropdown-city'>
            <h6 className='font-weight-bold mb-1'>City</h6>
            <Select
              options={cityOptions}
              value={selectedCity}
              onChange={(opt) => setFilters((f) => ({
                ...f, cityId: opt.value, neighborhoodIds: [], zipCodes: [], drawnShape: null, offset: 0,
              }))}
              classNamePrefix='default-select'
              menuPlacement='auto'
              styles={selectStyles}
              menuPortalTarget={document.body}
            />
          </div>
          <div className='location-dropdown-col'>
            <h6 className='font-weight-bold mb-1'>Neighborhoods</h6>
            <Select
              isMulti
              closeMenuOnSelect={false}
              options={neighborhoodOptions}
              value={selectedNeighborhoods}
              onChange={(opts) => setFilters((f) => ({ ...f, neighborhoodIds: opts.map((o) => o.value), offset: 0 }))}
              placeholder='All neighborhoods'
              isDisabled={!filters.cityId}
              classNamePrefix='default-select'
              menuPlacement='auto'
              styles={selectStyles}
              menuPortalTarget={document.body}
            />
          </div>
          <div className='location-dropdown-col'>
            <h6 className='font-weight-bold mb-1'>ZIP Codes</h6>
            <Select
              isMulti
              closeMenuOnSelect={false}
              options={zipOptions}
              value={selectedZips}
              onChange={(opts) => setFilters((f) => ({ ...f, zipCodes: opts.map((o) => o.value), offset: 0 }))}
              placeholder='All ZIP codes'
              isDisabled={!filters.cityId}
              classNamePrefix='default-select'
              menuPlacement='auto'
              styles={selectStyles}
              menuPortalTarget={document.body}
            />
          </div>
        </div>
        {/* Row 2: Mini-map */}
        <LocationMapPanel
          filters={filters}
          setFilters={setFilters}
          cityOptions={cityOptions}
          neighborhoodOptions={neighborhoodOptions}
          zipOptions={zipOptions}
          onExpandChange={onExpandChange}
          totalMatched={totalMatched}
        />
      </div>
    </div>
  );
}

function SearchContent({ filters, setFilters, activeFilterPill, alwaysVisible }) {
  const set = (key, value) => setFilters((f) => ({ ...f, [key]: value, offset: 0 }));
  return (
    <div
      className={`filter-box-container${alwaysVisible ? '' : activeFilterPill !== 'search' ? ' hidden' : ''}`}
      style={{ left: 0 }}
    >
      <div className='filter-box'>
        <h6 className='font-weight-bold mb-2'>Search by name</h6>
        <div className='inputs-icons'>
          <InputGroup>
            <InputGroupText><Icon icon='search' /></InputGroupText>
            <DebounceInput
              type='text'
              className='form-control'
              autoComplete='off'
              placeholder='Type a property name...'
              debounceTimeout={400}
              value={filters.search}
              onChange={(e) => set('search', e.target.value)}
            />
          </InputGroup>
        </div>
      </div>
    </div>
  );
}

function PriceContent({ filters, setFilters }) {
  const set = (key, value) => setFilters((f) => ({ ...f, [key]: value, offset: 0 }));
  return (
    <div className='filter-box-container' style={{ left: 0 }}>
      <div className='filter-box'>
        <h6 className='mr-1 font-weight-bold d-inline-block'>Price</h6>
        {!filters.minPrice && !filters.maxPrice && <span className='text-muted'> - Any</span>}
        <div className='px-2 py-2 mt-2 mb-1 w-100'>
          <Nouislider
            connect
            tooltips
            start={[filters.minPrice || 0, filters.maxPrice || 10000]}
            behaviour='tap'
            range={{ min: [0, 100], '5%': [100, 100], '50%': [2600, 200], '75%': [5000, 500], max: [10000] }}
            onChange={(e) => {
              const mn = parseFloat(e[0]) < 100 ? null : Math.round(parseFloat(e[0]));
              const mx = parseFloat(e[1]) > 9500 ? null : Math.round(parseFloat(e[1]));
              setFilters((f) => ({ ...f, minPrice: mn, maxPrice: mx, offset: 0 }));
            }}
            format={{ to: (v) => (v === undefined ? '' : v.toFixed(0)), from: Number }}
          />
        </div>
        <Row className='mt-3'>
          <Col xs='6' className='inputs-icons'>
            <InputGroup>
              <InputGroupText><Icon icon='dollar-sign' /></InputGroupText>
              <DebounceInput
                type='text'
                className='form-control'
                autoComplete='off'
                placeholder='Min Price'
                debounceTimeout={400}
                value={filters.minPrice ? numberFormatter.format(filters.minPrice) : ''}
                onChange={(e) => {
                  const v = Number(e.target.value?.replace(/,/g, ''));
                  if (v <= 0) { set('minPrice', null); return; }
                  if (v > filters.maxPrice) { set('maxPrice', v); }
                  set('minPrice', v);
                }}
              />
            </InputGroup>
          </Col>
          <Col xs='6' className='inputs-icons'>
            <InputGroup>
              <InputGroupText><Icon icon='dollar-sign' /></InputGroupText>
              <DebounceInput
                type='text'
                className='form-control'
                autoComplete='off'
                placeholder='Max Price'
                debounceTimeout={1000}
                value={filters.maxPrice ? numberFormatter.format(filters.maxPrice) : ''}
                onChange={(e) => {
                  const v = Number(e.target.value?.replace(/,/g, ''));
                  if (v <= 0) { set('maxPrice', null); return; }
                  if (v < filters.minPrice) { set('minPrice', v); }
                  set('maxPrice', v);
                }}
              />
            </InputGroup>
          </Col>
        </Row>
      </div>
    </div>
  );
}

function BedBathContent({ filters, setFilters, activeFilterPill, alwaysVisible }) {
  const set = (key, value) => setFilters((f) => ({ ...f, [key]: value, offset: 0 }));
  return (
    <div
      className={`filter-box-container${alwaysVisible ? '' : activeFilterPill !== 'bedbath' ? ' hidden' : ''}`}
      style={{ left: 0 }}
    >
      <div className='filter-box'>
        <Row>
          <Col xs='6'>
            <h6 className='font-weight-bold'>Bed Size</h6>
            {BED_OPTIONS.map(([code, label]) => (
              <div key={code}>
                <Label>
                  <input
                    className='custom-checkbox d-inline-block w-auto mr-1'
                    type='checkbox'
                    checked={filters.bedTypes.includes(code)}
                    onChange={() => {
                      const next = filters.bedTypes.includes(code)
                        ? filters.bedTypes.filter((b) => b !== code)
                        : [...filters.bedTypes, code];
                      set('bedTypes', next);
                    }}
                  /> {label}
                </Label>
              </div>
            ))}
          </Col>
          <Col xs='6'>
            <h6 className='font-weight-bold'>Bath Size</h6>
            {BATH_OPTIONS.map((v) => (
              <div key={v}>
                <Label>
                  <input
                    className='d-inline-block w-auto mr-1'
                    type='radio'
                    name='fp-bath'
                    checked={filters.minBaths === parseFloat(v)}
                    onChange={() => set('minBaths', parseFloat(v))}
                  /> {v}+ Bath
                </Label>
              </div>
            ))}
          </Col>
        </Row>
      </div>
    </div>
  );
}

// Max date = last day of the 12th month from today (e.g. Apr 2026 → Mar 31 2027)
const MOVE_IN_MAX_DATE = new Date(new Date().getFullYear(), new Date().getMonth() + 12, 0);

function MoveInContent({ filters, setFilters, activeFilterPill, alwaysVisible }) {
  // Captures changeMonth/changeYear from renderCustomHeader so the "Today" link
  // can navigate the inline calendar back to the current month without touching selection.
  const navigateToToday = useRef(null);

  const onChange = (dates) => {
    const [start, end] = dates;
    setFilters((f) => ({ ...f, availFrom: start || null, availTo: end || null, offset: 0 }));
  };

  return (
    <div
      className={`filter-box-container${alwaysVisible ? '' : activeFilterPill !== 'movein' ? ' hidden' : ''}`}
      style={{ left: 0 }}
    >
      <div className='filter-box'>
        <div className='d-flex align-items-start justify-content-between'>
          <div>
            <h6 className='font-weight-bold mb-1'>Move-in Date</h6>
            <p className='text-muted mb-2' style={{ fontSize: 14 }}>
              Earliest – Latest Move Date Range
            </p>
          </div>
          <a
            href='#'
            style={{ fontSize: '0.9rem', whiteSpace: 'nowrap', marginTop: 20 }}
            onClick={(e) => { e.preventDefault(); navigateToToday.current?.(); }}
          >
            &laquo; Current month
          </a>
        </div>
        <DatePicker
          selected={filters.availFrom || null}
          startDate={filters.availFrom}
          endDate={filters.availTo}
          selectsRange
          inline
          onChange={onChange}
          minDate={new Date()}
          maxDate={MOVE_IN_MAX_DATE}
          calendarClassName='filter-bar-calendar no-border'
          disabledKeyboardNavigation
          renderCustomHeader={({ date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled, changeMonth, changeYear }) => {
            // Capture navigation callbacks on every render so the ref stays current
            navigateToToday.current = () => {
              const today = new Date();
              changeYear(today.getFullYear());
              changeMonth(today.getMonth());
            };
            return (
              <div className='react-datepicker__header--custom-nav'>
                <button
                  type='button'
                  className='react-datepicker__navigation react-datepicker__navigation--previous'
                  onClick={decreaseMonth}
                  disabled={prevMonthButtonDisabled}
                >
                  <span className='react-datepicker__navigation-icon react-datepicker__navigation-icon--previous' />
                </button>
                <span className='react-datepicker__current-month'>
                  {moment(date).format('MMMM YYYY')}
                </span>
                <button
                  type='button'
                  className='react-datepicker__navigation react-datepicker__navigation--next'
                  onClick={increaseMonth}
                  disabled={nextMonthButtonDisabled}
                >
                  <span className='react-datepicker__navigation-icon react-datepicker__navigation-icon--next' />
                </button>
              </div>
            );
          }}
        />
      </div>
    </div>
  );
}

function UnitSizeContent({ filters, setFilters, activeFilterPill, alwaysVisible }) {
  const set = (key, value) => setFilters((f) => ({ ...f, [key]: value, offset: 0 }));
  return (
    <div
      className={`filter-box-container${alwaysVisible ? '' : activeFilterPill !== 'unitsize' ? ' hidden' : ''}`}
      style={{ left: 0 }}
    >
      <div className='filter-box' style={{ marginLeft: '-257px' }}>
        <h6 className='font-weight-bold d-inline-block mr-2'>Unit Size (sqft)</h6>
        <span className='text-muted'>
          {filters.minSqft
            ? `Larger than ${numberFormatter.format(filters.minSqft)} sqft`
            : 'Any'}
        </span>
        <div className='px-2 py-2 mt-2 w-100'>
          <Nouislider
            connect='lower'
            tooltips
            start={[filters.minSqft || 0]}
            behaviour='tap'
            range={{
              min: [0, 100],
              '5%': [100, 100],
              '15%': [400, 50],
              '75%': [1300, 100],
              '90%': [2000, 500],
              max: [5000],
            }}
            onChange={(e) => {
              const v = parseFloat(e[0]);
              set('minSqft', v < 100 ? null : Math.round(v));
            }}
            format={{ to: (v) => (v === undefined ? '' : v.toFixed(0)), from: Number }}
          />
        </div>
      </div>
    </div>
  );
}

function AmenitiesContent({ filters, setFilters, activeFilterPill, alwaysVisible }) {
  const [search, setSearch] = useState('');

  const getState = (amenity) => {
    if (amenity.search.some((t) => filters.amenities.includes(t))) return 'include';
    if (amenity.search.some((t) => (filters.excludeAmenities || []).includes(t))) return 'exclude';
    return null;
  };

  const handleToggle = (amenity) => {
    const state = getState(amenity);
    const terms = amenity.search;
    if (state === null) {
      setFilters((f) => ({ ...f, amenities: [...f.amenities, ...terms], offset: 0 }));
    } else if (state === 'include') {
      setFilters((f) => ({
        ...f,
        amenities: f.amenities.filter((t) => !terms.includes(t)),
        excludeAmenities: [...(f.excludeAmenities || []), ...terms],
        offset: 0,
      }));
    } else {
      setFilters((f) => ({
        ...f,
        excludeAmenities: (f.excludeAmenities || []).filter((t) => !terms.includes(t)),
        offset: 0,
      }));
    }
  };

  const includedCount = defaultAmenities.filter((a) => getState(a) === 'include').length;
  const excludedCount = defaultAmenities.filter((a) => getState(a) === 'exclude').length;

  const filtered = defaultAmenities.filter((a) =>
    a.name.toLowerCase().includes(search.toLowerCase())
  );
  const sorted = [
    ...filtered.filter((a) => getState(a) === 'include'),
    ...filtered.filter((a) => getState(a) === 'exclude'),
    ...filtered.filter((a) => getState(a) === null && a.popular),
    ...filtered.filter((a) => getState(a) === null && !a.popular),
  ];

  return (
    <div className={`filter-box-container${alwaysVisible ? '' : activeFilterPill !== 'amenities' ? ' hidden' : ''}`}>
      <div className='filter-box' style={{ marginLeft: '-257px' }}>
        <div className='d-flex justify-content-between align-items-center mb-2'>
          <h6 className='font-weight-bold mb-0'>Amenities</h6>
          <div className='d-flex align-items-center' style={{ gap: 6 }}>
            {includedCount > 0 && <span className='amenity-count-include'>{includedCount} included</span>}
            {excludedCount > 0 && <span className='amenity-count-exclude'>{excludedCount} excluded</span>}
          </div>
        </div>
        <div className='inputs-icons mb-2'>
          <InputGroup>
            <InputGroupText><Icon icon='search' /></InputGroupText>
            <Input
              type='text'
              autoComplete='off'
              placeholder='Search amenities...'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
        </div>
        <div className='amenity-list border rounded'>
          {sorted.map((amenity) => {
            const state = getState(amenity);
            return (
              <div key={amenity.name} className='amenity-row' onClick={() => handleToggle(amenity)}>
                <div className={`amenity-checkbox${state ? ' ' + state : ''}`}>
                  {state === 'include' && <Icon icon='check' />}
                  {state === 'exclude' && <Icon icon='times' />}
                </div>
                <span>{amenity.name}</span>
                {state === null && amenity.popular && <Icon icon='star' className='amenity-star' />}
              </div>
            );
          })}
        </div>
        <p className='text-muted mt-2 mb-0' style={{ fontSize: 13, lineHeight: 1.5 }}>
          Click once to&nbsp;<span className='amenity-count-include'><Icon icon='check' /></span>&nbsp;include,
          again to&nbsp;<span className='amenity-count-exclude'><Icon icon='times' /></span>&nbsp;exclude,
          and once more to clear the selection.
        </p>
      </div>
    </div>
  );
}

function MoreContent({ filters, setFilters, activeFilterPill, alwaysVisible }) {
  const currentYear = new Date().getFullYear();
  const set = (key, value) => setFilters((f) => ({ ...f, [key]: value, offset: 0 }));
  const { data: policiesData } = useQuery(PROPERTY_POLICIES);
  const policyOptions = (policiesData?.propertyPolicies || []).map((p) => ({ value: p.name, label: p.name }));
  const selectedPolicies = policyOptions.filter((o) => (filters.policies || []).includes(o.value));

  return (
    <div className={`filter-box-container${alwaysVisible ? '' : activeFilterPill !== 'more' ? ' hidden' : ''}`}>
      <div className='filter-box' style={{ marginLeft: '-255px' }}>
        <h6 className='font-weight-bold'>Property</h6>
        <Row className='mt-2'>
          <Col xs='6' className='inputs-icons-right'>
            <Label>Minimum Escort</Label>
            <InputGroup>
              <DebounceInput
                type='number'
                className='form-control'
                autoComplete='off'
                placeholder='Escort'
                debounceTimeout={400}
                min='0'
                value={filters.minEscort || ''}
                onChange={(e) => set('minEscort', e.target.value ? parseInt(e.target.value) : null)}
              />
              <InputGroupText><Icon size='sm' icon='percent' /></InputGroupText>
            </InputGroup>
          </Col>
          <Col xs='6' className='inputs-icons-right'>
            <Label>Minimum Send</Label>
            <InputGroup>
              <DebounceInput
                type='number'
                className='form-control'
                autoComplete='off'
                placeholder='Send'
                debounceTimeout={400}
                min='0'
                value={filters.minSend || ''}
                onChange={(e) => set('minSend', e.target.value ? parseInt(e.target.value) : null)}
              />
              <InputGroupText><Icon size='sm' icon='percent' /></InputGroupText>
            </InputGroup>
          </Col>
        </Row>
        <Row className='mt-3'>
          <Col md='12' className='inputs-icons'>
            <Label>Flat Fee</Label>
            <InputGroup>
              <InputGroupText><Icon icon='dollar-sign' /></InputGroupText>
              <DebounceInput
                type='text'
                className='form-control'
                autoComplete='off'
                placeholder='Flat Fee'
                debounceTimeout={400}
                value={filters.minBonus ? numberFormatter.format(filters.minBonus) : ''}
                onChange={(e) => {
                  const v = Number(e.target.value?.replace(/,/g, ''));
                  set('minBonus', v > 0 ? v : null);
                }}
              />
            </InputGroup>
          </Col>
        </Row>
        <Row className='mt-3'>
          <Col md='6'>
            <Label>Built After – {filters.builtAfter || 'Any'}</Label>
            <div className='px-2 py-2 mb-1 w-100'>
              <Nouislider
                connect='lower' tooltips
                start={[filters.builtAfter || currentYear]}
                behaviour='tap'
                range={{ min: 1980, max: currentYear }}
                onChange={(e) => {
                  const v = parseInt(e[0]);
                  set('builtAfter', v === currentYear ? null : v);
                }}
                format={{ to: (v) => v?.toFixed(0) ?? '', from: Number }}
              />
            </div>
          </Col>
          <Col md='6'>
            <Label>Renovated After – {filters.renovatedAfter || 'Any'}</Label>
            <div className='px-2 py-2 mb-1 w-100'>
              <Nouislider
                connect='lower' tooltips
                start={[filters.renovatedAfter || currentYear]}
                behaviour='tap'
                range={{ min: 1980, max: currentYear }}
                onChange={(e) => {
                  const v = parseInt(e[0]);
                  set('renovatedAfter', v === currentYear ? null : v);
                }}
                format={{ to: (v) => v?.toFixed(0) ?? '', from: Number }}
              />
            </div>
          </Col>
        </Row>
        <h6 className='font-weight-bold mt-3'>Policies</h6>
        <Select
          isMulti
          closeMenuOnSelect={false}
          options={policyOptions}
          value={selectedPolicies}
          onChange={(opts) => set('policies', opts.map((o) => o.value))}
          placeholder='Policies'
          className='basic-multi-select w-100'
          classNamePrefix='default-select'
          menuPlacement='auto'
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          menuPortalTarget={document.body}
        />
      </div>
    </div>
  );
}

// ── FilterBar ───────────────────────────────────────────────────────────────
export default function FilterBar({ filters, setFilters, onMapExpand, totalMatched = 0 }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeFilterPill, setActiveFilterPill] = useState(null);
  const barRef = useRef(null);

  // ── Data fetches ──────────────────────────────────────────────────────────
  const { data: citiesData } = useQuery(CITIES);
  const { data: neighborhoodsData } = useQuery(NEIGHBORHOODS, {
    variables: { cityId: filters.cityId },
    skip: !filters.cityId,
  });
  const { data: zipCodesData } = useQuery(ZIP_CODES, {
    variables: { cityId: filters.cityId },
    skip: !filters.cityId,
  });

  // Default city on load
  useEffect(() => {
    if (citiesData?.cities?.length > 0 && !filters.cityId) {
      const savedId = localStorage.getItem('default_city_id');
      const city = savedId
        ? citiesData.cities.find((c) => String(c.id) === String(savedId)) || citiesData.cities[0]
        : citiesData.cities[0];
      setFilters((f) => ({ ...f, cityId: parseInt(city.id), offset: 0 }));
    }
  }, [citiesData]);

  // Close active pill on outside click. Skipped while the mobile modal is
  // open — clicks inside the modal land outside barRef and would otherwise
  // collapse the accordion on every interaction.
  useEffect(() => {
    function handle(e) {
      if (mobileOpen) return;
      if (barRef.current && !barRef.current.contains(e.target)) {
        setActiveFilterPill(null);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [mobileOpen]);

  // Reset the accordion to all-collapsed each time the modal opens.
  useEffect(() => {
    if (mobileOpen) setActiveFilterPill(null);
  }, [mobileOpen]);

  // The mobile bar/modal renders below 1200px (Bootstrap's d-xl-none). If the
  // user resizes into the desktop range while the modal is open, the modal
  // markup ends up overlaying the desktop bar with stale, mobile-styled
  // content — close it on the breakpoint crossing.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1200px)');
    const handle = (e) => { if (e.matches) setMobileOpen(false); };
    if (mq.addEventListener) {
      mq.addEventListener('change', handle);
      return () => mq.removeEventListener('change', handle);
    }
    // Safari < 14 fallback
    mq.addListener(handle);
    return () => mq.removeListener(handle);
  }, []);

  const set = (key, value) => setFilters((f) => ({ ...f, [key]: value, offset: 0 }));
  // Only one pill open at a time; clicking the active pill closes it
  const togglePill = (name) => setActiveFilterPill((a) => (a === name ? null : name));

  // ── Derived option arrays for LocationContent ─────────────────────────────
  const cityOptions = (citiesData?.cities || []).map((c) => ({ value: parseInt(c.id), label: c.name }));
  const neighborhoodOptions = (neighborhoodsData?.neighborhoods || []).map((n) => ({
    value: parseInt(n.id), label: n.name,
  }));
  const zipOptions = (zipCodesData?.zipCodes || []).map((z) => ({ value: z, label: z }));
  const selectedCity = cityOptions.find((o) => o.value === filters.cityId) || null;

  // ── Pill label helpers ────────────────────────────────────────────────────
  // City IS shown in the subtitle (it's a meaningful display value), but it's
  // not counted toward the badge nor does it trigger the clear-(×) button —
  // clearing the city is a no-op since it always snaps back to a default.
  // See `hasLocationNarrowing` and `canClear` on the Location FilterPill.
  const locationLabel = () => {
    const parts = [];
    if (selectedCity) parts.push(selectedCity.label);
    if (filters.neighborhoodIds.length > 0) parts.push(`${filters.neighborhoodIds.length} nbhd`);
    if (filters.zipCodes.length > 0) parts.push(`${filters.zipCodes.length} ZIP`);
    if (filters.drawnShape?.length > 0) parts.push('Custom area');
    return parts.join(' · ') || '';
  };

  const priceLabel = () => {
    if (filters.minPrice && filters.maxPrice)
      return `${currencyFormatter.format(filters.minPrice)}–${currencyFormatter.format(filters.maxPrice)}`;
    if (filters.minPrice) return `${currencyFormatter.format(filters.minPrice)} & above`;
    if (filters.maxPrice) return `Below ${currencyFormatter.format(filters.maxPrice)}`;
    return '';
  };

  const bedBathLabel = () => {
    const parts = [];
    if (filters.bedTypes.length > 0) parts.push(filters.bedTypes.map((b) => unitTypes[b]).join(', '));
    if (filters.minBaths) parts.push(`${filters.minBaths}+ Bath`);
    return parts.join(' · ');
  };

  const moveInLabel = () => {
    const { availFrom, availTo } = filters;
    if (availFrom && availTo) {
      if (moment(availFrom).format('MMM D') === moment(availTo).format('MMM D'))
        return `Available on ${moment(availFrom).format('MMM D')}`;
      if (new Date(availFrom) <= new Date())
        return `Before ${moment(availTo).format('MMM D')}`;
      return `${moment(availFrom).format('MMM D')}-${moment(availTo).format('MMM D')}`;
    }
    if (availFrom) return `${moment(availFrom).format('MMM D')} & after`;
    if (availTo)   return `Before ${moment(availTo).format('MMM D')}`;
    return '';
  };

  const sqftLabel = () =>
    filters.minSqft ? `${numberFormatter.format(filters.minSqft)} sqft+` : '';

  const amenityLabel = () => {
    const inc = defaultAmenities.filter((a) =>
      a.search.some((s) => filters.amenities.includes(s))
    ).length;
    const exc = defaultAmenities.filter((a) =>
      a.search.some((s) => (filters.excludeAmenities || []).includes(s))
    ).length;
    const parts = [];
    if (inc > 0) parts.push(`${inc} included`);
    if (exc > 0) parts.push(`${exc} excluded`);
    return parts.join(' · ');
  };

  const moreLabel = () => {
    const parts = [];
    if (filters.minEscort)        parts.push(`Escort ${filters.minEscort}%+`);
    if (filters.minSend)          parts.push(`Send ${filters.minSend}%+`);
    if (filters.minBonus)         parts.push(`Fee $${numberFormatter.format(filters.minBonus)}+`);
    if (filters.builtAfter)       parts.push(`Built ${filters.builtAfter}+`);
    if (filters.renovatedAfter)   parts.push(`Renov ${filters.renovatedAfter}+`);
    if (filters.policies?.length) parts.push(`${filters.policies.length} polic${filters.policies.length === 1 ? 'y' : 'ies'}`);
    return parts.join(', ');
  };

  const hasAnyFilter =
    filters.search ||
    filters.neighborhoodIds.length > 0 || filters.zipCodes.length > 0 ||
    filters.drawnShape?.length > 0 ||
    filters.minPrice || filters.maxPrice ||
    filters.bedTypes.length > 0 || filters.minBaths || filters.minSqft ||
    filters.availFrom || filters.availTo || filters.amenities.length > 0 ||
    filters.builtAfter || filters.renovatedAfter ||
    filters.minEscort || filters.minSend || filters.minBonus ||
    filters.policies?.length > 0;

  // City is intentionally excluded from "what counts as a location filter":
  // it's always set to a default, so its presence shouldn't make the pill
  // appear filtered or clearable. Used both for badge count and for
  // `canClear` on the Location pill (desktop + mobile).
  const hasLocationNarrowing =
    filters.neighborhoodIds.length > 0 ||
    filters.zipCodes.length > 0 ||
    filters.drawnShape?.length > 0;

  // Count of distinct active filter sections (1 per accordion card with a value).
  // Search-by-name IS counted because it actively narrows results.
  const activeFilterCount = [
    Boolean(filters.search),
    hasLocationNarrowing,
    Boolean(priceLabel()),
    Boolean(bedBathLabel()),
    Boolean(moveInLabel()),
    Boolean(sqftLabel()),
    Boolean(amenityLabel()),
    Boolean(moreLabel()),
  ].filter(Boolean).length;

  const clearAll = () => {
    setActiveFilterPill(null);
    setFilters((f) => ({
      ...f,
      search: '', neighborhoodIds: [], zipCodes: [], drawnShape: [],
      minPrice: null, maxPrice: null, bedTypes: [], minBaths: null,
      minSqft: null,
      amenities: [], excludeAmenities: [],
      policies: [], minEscort: null, minSend: null, minBonus: null,
      availFrom: null, availTo: null, builtAfter: null, renovatedAfter: null,
      offset: 0,
    }));
  };

  // The expanded map overlay is rendered by LocationMapPanel which lives
  // inside this modal's React subtree. Closing the modal would unmount
  // LocationMapPanel and force-destroy the overlay's portal child along with
  // it — so we deliberately leave mobileOpen alone on expand. The overlay's
  // higher z-index (1600 vs the modal's 1500) covers the modal full-screen,
  // and on Back the overlay unmounts cleanly with the modal still mounted
  // underneath.

  // Prop bundles passed down to content components
  const locationProps = {
    filters, setFilters, cityOptions, neighborhoodOptions, zipOptions,
    onExpandChange: onMapExpand,
    totalMatched,
  };

  // ── Desktop bar ───────────────────────────────────────────────────────────
  return (
    <>
      <div
        className='py-3 border-bottom fixed-top filter-bar-container d-none d-xl-block'
        style={{ top: '65px', zIndex: 10 }}
        ref={barRef}
      >
        <div className='text-center px-3'>
          <div className={`filter-bar ${activeFilterPill ? 'active' : ''}`}>
            {/* Search */}
            <div className='search-bar'>
              <FormGroup className='mb-0 d-inline-flex flex-grow-1 align-items-center'>
                <Label className='mb-0 w-auto d-inline-flex mr-1'>
                  <Icon icon='search' />
                </Label>
                <DebounceInput
                  type='text'
                  autoComplete='off'
                  placeholder='Search by property name'
                  debounceTimeout={400}
                  value={filters.search}
                  onChange={(e) => set('search', e.target.value)}
                />
              </FormGroup>
            </div>

            <div className='applied-filters filters d-flex text-left align-items-center'>
              {/* Location */}
              <FilterPill
                filterLabel='Location/Area'
                filterValue={locationLabel()}
                canClear={hasLocationNarrowing}
                resetFilter={() => setFilters((f) => ({ ...f, neighborhoodIds: [], zipCodes: [], drawnShape: [], offset: 0 }))}
                active={activeFilterPill === 'location'}
                onClick={() => togglePill('location')}
              >
                {activeFilterPill === 'location' && <LocationContent {...locationProps} />}
              </FilterPill>

              {/* Price */}
              <FilterPill
                filterLabel='Price'
                filterValue={priceLabel()}
                resetFilter={() => setFilters((f) => ({ ...f, minPrice: null, maxPrice: null, offset: 0 }))}
                active={activeFilterPill === 'price'}
                onClick={() => togglePill('price')}
              >
                {activeFilterPill === 'price' && <PriceContent filters={filters} setFilters={setFilters} />}
              </FilterPill>

              {/* Beds/Baths */}
              <FilterPill
                filterLabel='Bed/Bath'
                filterValue={bedBathLabel()}
                resetFilter={() => setFilters((f) => ({ ...f, bedTypes: [], minBaths: null, offset: 0 }))}
                active={activeFilterPill === 'bedbath'}
                onClick={() => togglePill('bedbath')}
              >
                <BedBathContent filters={filters} setFilters={setFilters} activeFilterPill={activeFilterPill} />
              </FilterPill>

              {/* Move-in Date */}
              <FilterPill
                filterLabel='Move-in Date'
                filterValue={moveInLabel()}
                resetFilter={() => setFilters((f) => ({ ...f, availFrom: null, availTo: null, offset: 0 }))}
                active={activeFilterPill === 'movein'}
                onClick={() => togglePill('movein')}
              >
                <MoveInContent filters={filters} setFilters={setFilters} activeFilterPill={activeFilterPill} />
              </FilterPill>

              {/* Unit Size */}
              <FilterPill
                filterLabel='Unit Size'
                filterValue={sqftLabel()}
                resetFilter={() => set('minSqft', null)}
                active={activeFilterPill === 'unitsize'}
                onClick={() => togglePill('unitsize')}
              >
                <UnitSizeContent filters={filters} setFilters={setFilters} activeFilterPill={activeFilterPill} />
              </FilterPill>

              {/* Amenities */}
              <FilterPill
                filterLabel='Amenities'
                filterValue={amenityLabel()}
                resetFilter={() => setFilters((f) => ({ ...f, amenities: [], excludeAmenities: [], offset: 0 }))}
                active={activeFilterPill === 'amenities'}
                onClick={() => togglePill('amenities')}
              >
                <AmenitiesContent filters={filters} setFilters={setFilters} activeFilterPill={activeFilterPill} />
              </FilterPill>

              {/* More Filters */}
              <FilterPill
                filterLabel='More Filters'
                filterValue={moreLabel()}
                resetFilter={() => setFilters((f) => ({
                  ...f,
                  minEscort: null, minSend: null, minBonus: null,
                  builtAfter: null, renovatedAfter: null,
                  policies: [],
                  offset: 0,
                }))}
                active={activeFilterPill === 'more'}
                onClick={() => togglePill('more')}
              >
                <MoreContent filters={filters} setFilters={setFilters} activeFilterPill={activeFilterPill} />
              </FilterPill>
            </div>

            {/* Search / Clear button */}
            <div className='search-button d-flex align-items-center'>
              {hasAnyFilter ? (
                <Button className='h-100 w-100 btn btn-danger' onClick={clearAll}>
                  Clear
                </Button>
              ) : (
                <Button className='h-100 w-100 btn btn-blue' onClick={() => setActiveFilterPill(null)}>
                  <Icon icon='search' />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Mobile bar ──────────────────────────────────────────────────────── */}
      <div
        className='py-3 border-bottom fixed-top filter-bar-container d-xl-none'
        style={{ top: '65px', zIndex: 10 }}
      >
        <div className='text-center px-3 d-flex align-items-center' style={{ gap: 8 }}>
          <div className={`filter-bar mr-2 ${activeFilterPill ? 'active' : ''}`} style={{ flex: 1 }}>
            <div className='search-bar'>
              <FormGroup className='mb-0 d-inline-flex flex-grow-1 align-items-center'>
                <Label className='mb-0 w-auto d-inline-flex mr-1'>
                  <Icon icon='search' />
                </Label>
                <DebounceInput
                  type='text'
                  autoComplete='off'
                  placeholder='Search by property name'
                  debounceTimeout={400}
                  value={filters.search}
                  onChange={(e) => set('search', e.target.value)}
                />
              </FormGroup>
            </div>
            <div className='search-button'>
              {hasAnyFilter ? (
                <Button className='h-100 w-100 btn btn-danger' onClick={clearAll}>
                  Clear
                </Button>
              ) : (
                <Button className='h-100 w-100 btn btn-blue'>
                  <Icon icon='search' />
                </Button>
              )}
            </div>
          </div>
          <div className='filterIcon position-relative' onClick={() => setMobileOpen(true)}>
            <Icon icon='sliders-h' style={{ width: '26px', height: '38px' }} />
            {activeFilterCount > 0 && (
              <span className='filter-count-badge'>{activeFilterCount}</span>
            )}
          </div>
        </div>
      </div>

      {/* ── Mobile Modal ────────────────────────────────────────────────────── */}
      <Modal
        isOpen={mobileOpen}
        toggle={() => setMobileOpen(false)}
        className='modal-fullscreen modal-dialog-centered filter-modal'
        zIndex={1500}
        style={{ margin: '1.5rem', maxWidth: '100%' }}
      >
        <ModalHeader
          toggle={() => setMobileOpen(false)}
          className='font-weight-bold filter-modal-header'
        >
          All Filters
        </ModalHeader>
        <ModalBody className='bg-white pb-0 filter-modal-accordion'>
          <FilterPill mobile filterLabel='Name' filterValue={filters.search}
            resetFilter={() => set('search', '')}
            active={activeFilterPill === 'search'} onClick={() => togglePill('search')}>
            <SearchContent filters={filters} setFilters={setFilters} alwaysVisible />
          </FilterPill>
          <FilterPill mobile filterLabel='Location/Area' filterValue={locationLabel()}
            canClear={hasLocationNarrowing}
            resetFilter={() => setFilters((f) => ({ ...f, neighborhoodIds: [], zipCodes: [], drawnShape: [], offset: 0 }))}
            active={activeFilterPill === 'location'} onClick={() => togglePill('location')}>
            <LocationContent {...locationProps} />
          </FilterPill>
          <FilterPill mobile filterLabel='Price' filterValue={priceLabel()}
            resetFilter={() => setFilters((f) => ({ ...f, minPrice: null, maxPrice: null, offset: 0 }))}
            active={activeFilterPill === 'price'} onClick={() => togglePill('price')}>
            <PriceContent filters={filters} setFilters={setFilters} />
          </FilterPill>
          <FilterPill mobile filterLabel='Bed/Bath' filterValue={bedBathLabel()}
            resetFilter={() => setFilters((f) => ({ ...f, bedTypes: [], minBaths: null, offset: 0 }))}
            active={activeFilterPill === 'bedbath'} onClick={() => togglePill('bedbath')}>
            <BedBathContent filters={filters} setFilters={setFilters} alwaysVisible />
          </FilterPill>
          <FilterPill mobile filterLabel='Move-in Date' filterValue={moveInLabel()}
            resetFilter={() => setFilters((f) => ({ ...f, availFrom: null, availTo: null, offset: 0 }))}
            active={activeFilterPill === 'movein'} onClick={() => togglePill('movein')}>
            <MoveInContent filters={filters} setFilters={setFilters} alwaysVisible />
          </FilterPill>
          <FilterPill mobile filterLabel='Unit Size' filterValue={sqftLabel()}
            resetFilter={() => set('minSqft', null)}
            active={activeFilterPill === 'unitsize'} onClick={() => togglePill('unitsize')}>
            <UnitSizeContent filters={filters} setFilters={setFilters} alwaysVisible />
          </FilterPill>
          <FilterPill mobile filterLabel='Amenities' filterValue={amenityLabel()}
            resetFilter={() => setFilters((f) => ({ ...f, amenities: [], excludeAmenities: [], offset: 0 }))}
            active={activeFilterPill === 'amenities'} onClick={() => togglePill('amenities')}>
            <AmenitiesContent filters={filters} setFilters={setFilters} alwaysVisible />
          </FilterPill>
          <FilterPill mobile filterLabel='More Filters' filterValue={moreLabel()}
            resetFilter={() => setFilters((f) => ({ ...f, minEscort: null, minSend: null, minBonus: null, builtAfter: null, renovatedAfter: null, policies: [], offset: 0 }))}
            active={activeFilterPill === 'more'} onClick={() => togglePill('more')}>
            <MoreContent filters={filters} setFilters={setFilters} alwaysVisible />
          </FilterPill>
        </ModalBody>
        <ModalFooter className='modal-footer-sticky filter-modal-footer'>
          <Button
            className='btn btn-danger filter-modal-clear-btn'
            disabled={!hasAnyFilter}
            onClick={clearAll}
          >
            Clear all
          </Button>
          <Button
            className='btn btn-blue filter-modal-cta-btn'
            onClick={() => setMobileOpen(false)}
          >
            View {totalMatched.toLocaleString()} {totalMatched === 1 ? 'Property' : 'Properties'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
